import * as React from "react";
import { X } from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./index";
import { Button } from "../button";
import { cn } from "../../lib/utils";

export type DropdownOption = {
  label: React.ReactNode;
  value: string;
  disabled?: boolean;
  textValue?: string;
};

export interface DropdownProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  options: DropdownOption[];
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  onClear?: () => void;
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  optionClassName?: string;
  label?: React.ReactNode;
}

export function Dropdown({
  value,
  defaultValue,
  onValueChange,
  options,
  placeholder = "Select an option",
  disabled = false,
  clearable = false,
  onClear,
  className,
  triggerClassName,
  contentClassName,
  optionClassName,
  label,
}: DropdownProps) {
  const hasValue = value !== undefined && value !== "";

  return (
    <div className={cn("flex w-full flex-col gap-1.5", className)}>
      {label ? (
        <div className="text-sm font-medium text-foreground">{label}</div>
      ) : null}

      <div className="flex items-center gap-2">
        <Select
          value={value}
          defaultValue={defaultValue}
          onValueChange={onValueChange}
          disabled={disabled}
        >
          <SelectTrigger className={cn("h-10", triggerClassName)}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent className={contentClassName}>
            {options.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                textValue={option.textValue ?? String(option.label)}
                className={optionClassName}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {clearable && hasValue ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClear}
            disabled={disabled}
            className="h-10 w-10 shrink-0 p-0"
            aria-label="Clear selection"
          >
            <X className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export type { SelectItem };
